/**
 * Runtime theming: write brand CSS variables onto :root. Source can be a Setting
 * row (per-tenant brand) or a saved preference — no rebuild needed to rebrand.
 */
const PRIMARY_STEP = /^--color-primary-(\d+)$/;

/** '#2563eb' / '#26e' → '37 99 235' (the RGB triplet twreact-ui's --dt-* tokens hold); null otherwise. */
export function hexToTriplet(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)).join(' ');
}

/**
 * Normalise brand variables: each `--color-primary-<step>` hex also writes the matching
 * `--dt-primary-<step>` triplet, which every primary-* class (the app's and twreact-ui's) reads.
 */
export function brandVars(vars) {
  const out = {};
  for (const [key, v] of Object.entries(vars || {})) {
    const k = key.startsWith('--') ? key : `--${key}`;
    out[k] = v;
    const step = PRIMARY_STEP.exec(k);
    const triplet = step && hexToTriplet(v);
    if (triplet) out[`--dt-primary-${step[1]}`] = triplet;
  }
  return out;
}

export function applyBrand(vars) {
  if (!vars) return;
  const root = document.documentElement;
  for (const [k, v] of Object.entries(brandVars(vars))) root.style.setProperty(k, v);
}

const KEY = 'app:brand';

export function saveBrand(vars) {
  try {
    localStorage.setItem(KEY, JSON.stringify(vars));
  } catch {}
  applyBrand(vars);
}

export function restoreBrand() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (v) applyBrand(v);
  } catch {}
}

/** Optionally pull the brand from the backend Setting model (key='brand'). */
export async function loadBrandFromApi(fetchApi) {
  try {
    const res = await fetchApi('crud', { model: 'Setting', action: 'list', where: { key: 'brand' } });
    const rows = Array.isArray(res) ? res : res && res.data;
    const row = rows && rows[0];
    if (row && row.value) saveBrand(JSON.parse(row.value));
  } catch {}
}
